"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

// ─── Copy ─────────────────────────────────────────────────────────────────────

const STATS = [
  { value: "1,194", label: "Posts analyzed", sub: "Automations board" },
  { value: "548", label: "Grouped requests", sub: "54% noise removed" },
  { value: "134", label: "Already shipped", sub: "Auto-detected" },
  { value: "85", label: "UI/UX quick wins", sub: "Surfaced instantly" },
];

const PIPELINE_STEPS = [
  { n: "01", title: "Ingest", body: "Pull posts, comments, and changelogs from Canny across any board." },
  { n: "02", title: "Summarize", body: "GPT-4o reads each post and generates a structured summary that captures the core request, including comments." },
  { n: "03", title: "Deduplicate", body: "Two search methods find similar requests: AI similarity matching and keyword overlap. GPT-4o then confirms which pairs are truly the same." },
  { n: "04", title: "Extract Insights", body: "Customer pain points, needs, an impact score from 0 to 10, and business impact are extracted for every request." },
  { n: "05", title: "Detect Completion", body: "Published changelogs are compared against all open requests. Features that are already shipped get flagged automatically." },
  { n: "06", title: "Classify and Score", body: "Quick UI/UX wins are identified and separated. All groups are ranked by combined impact score." },
];

const AGENT_STEPS = [
  { icon: "📋", label: "PRD", model: "gpt-4o", desc: "Full product requirements document built from real customer pain points and needs" },
  { icon: "🗂", label: "Tasks", model: "gpt-4o-mini", desc: "Dev, QA, and Design tasks with story point estimates, sprint-ready" },
  { icon: "📄", label: "Spec", model: "gpt-4o", desc: "Feature specification file for each development task" },
  { icon: "📐", label: "Build Plan", model: "gpt-5.4", desc: "Step-by-step Cursor plan with file paths, test instructions, and coding guidance" },
];

const FAQ_ITEMS = [
  {
    q: "Why is this better than just sorting feature requests by vote count?",
    a: "Votes are useful, but duplicate posts fragment the signal. Ten customers requesting the same feature across ten separate posts each get their own small vote count — the true aggregate demand never surfaces. Roadmap Radar groups semantically similar requests first, then aggregates votes across the group. The result is a much truer picture of what customers actually want most.",
  },
  {
    q: "How are bad AI decisions prevented?",
    a: "The system is decision support, not autonomous product strategy. Every group shows the exact source posts it was built from, with direct Canny links. Nothing is hidden or black-boxed. PMs can verify any grouping decision in seconds. Outputs are validated against known datasets during rollout, and human review is built into every step.",
  },
  {
    q: "How is this different from AI coding tools like Copilot, Devin, or AI Lens?",
    a: "Those tools solve the how — how to implement a given task. Roadmap Radar solves the what — which tasks are worth implementing, in which order, based on the full picture of customer demand. The two are complementary. A team running an AI coding tool on top of Roadmap Radar gets both the right prioritization and the fast execution. A team using only an execution tool ships quickly — but from whatever was already on the backlog, not necessarily the highest-impact work.",
  },
  {
    q: "Does the AI write the code?",
    a: "No. The AI writes the plan, not the code. Plans are pushed to a dedicated GitHub repository that has zero access to the product codebase. Developers review the plan, decide whether to run it, and execute it in Cursor on their own machine. The AI handles the hard planning work. The developer owns the commit.",
  },
  {
    q: "What does Generate PRD actually produce?",
    a: "A full PRD covering the problem statement, user pain points, proposed solution, success metrics, and open questions. It also generates Fibonacci story-pointed dev, QA, and design sub-tasks — creating a ClickUp Epic with all sub-tasks and a QA card automatically. One Cursor-executable implementation plan is pushed to GitHub per dev task, with TDD steps, exact file paths, and test commands included.",
  },
  {
    q: "What signal sources are planned beyond Canny?",
    a: "The pipeline is source-agnostic by design. The deduplication and grouping logic works on any text-based signal. Planned additions include Reddit product discussions, YouTube comment threads, support ticket summaries, Pendo usage analytics, and GitHub issue cross-referencing. Every additional source deepens the demand picture and strengthens the prioritization signal.",
  },
  {
    q: "How long does a pipeline run take, and what does it cost?",
    a: "For a board with around 1,200 posts, the full pipeline takes 30 to 90 minutes and requires roughly 9,000 GPT-4o calls. Smaller boards are proportionally faster and cheaper. A Mock mode simulates all nine pipeline stages with realistic timing and no API calls at all — safe for demos and exploration with zero cost.",
  },
  {
    q: "Is this production-ready?",
    a: "GCP infrastructure is provisioned and deployment scripts are in place. The application can be pushed to Cloud Run today. The remaining work is GCS storage integration, Google OAuth configuration, and observability — roughly 4 to 6 days of engineering plus one admin IAM action. Feature development is complete.",
  },
];

const SOURCES_NOW = ["Canny boards"];
const SOURCES_NEXT = ["Reddit discussions", "YouTube comments", "Support tickets", "Pendo analytics", "GitHub issues", "Community Slack"];

// ─── Navbar ────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { href: "#problem", label: "Problem" },
  { href: "#solution", label: "Solution" },
  { href: "#pipeline", label: "Pipeline" },
  { href: "#agent", label: "Agent" },
  { href: "#vision", label: "Vision" },
  { href: "/docs", label: "Docs" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? "bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-sm shadow-slate-900/5"
        : "bg-transparent border-b border-white/10"
    }`}>
      <div className="max-w-5xl mx-auto px-8 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/ghl-logo.jpeg"
            alt="GoHighLevel"
            width={28}
            height={28}
            className="w-7 h-7 rounded-lg object-contain bg-[#021b49] p-0.5 ring-1 ring-white/10 group-hover:scale-105 transition-transform shadow-md"
          />
          <span className={`font-bold text-[14px] tracking-tight transition-colors ${scrolled ? "text-slate-900" : "text-white"}`}>
            Roadmap Radar
          </span>
        </Link>

        {/* Nav links — hidden on mobile */}
        <nav className="hidden sm:flex items-center gap-0.5">
          {NAV_LINKS.map((link) => {
            const isPage = link.href.startsWith("/");
            const className = `px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
              scrolled
                ? link.label === "Docs"
                  ? "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                : link.label === "Docs"
                  ? "text-blue-300 hover:text-white hover:bg-white/10"
                  : "text-white/60 hover:text-white hover:bg-white/10"
            }`;
            return isPage
              ? <Link key={link.href} href={link.href} className={className}>{link.label}</Link>
              : <a key={link.href} href={link.href} className={className}>{link.label}</a>;
          })}
        </nav>

        {/* CTA */}
        <Link href="/login"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold transition-all ${
            scrolled
              ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-200"
              : "bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-sm"
          }`}>
          Sign in
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
        </Link>
      </div>
    </header>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FunnelPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden scroll-smooth">

      <Navbar />

      {/* ── Hero ── */}
      <section className="relative bg-[#0a0f1e] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-600 opacity-[0.12] blur-[120px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-8 pt-32 pb-24">
          <div className="grid lg:grid-cols-[1fr_420px] gap-12 items-center">

            {/* ── Left: text ── */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-xs font-semibold text-blue-300 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                  Dev Nitro 2026 · GoHighLevel Internal Tool
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-400/20 text-xs font-semibold text-amber-300 backdrop-blur-sm">
                  🔥 PMs love this one tool
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.08] tracking-tight mb-6">
                GoHighLevel has 10,000+ customer requests.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  Now we know which ones actually matter.
                </span>
              </h1>

              <p className="text-lg text-slate-300 leading-relaxed mb-10">
                Automations alone has 3,000+ posts. Across all boards, customers are filing feedback
                faster than any team can manually read it.{" "}
                <strong className="text-white font-semibold">Roadmap Radar</strong> turns that noise into
                ranked, grouped, execution-ready intelligence — so every sprint starts with the
                highest-impact work, not the loudest requests.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10">
                <Link href="/login"
                  className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-xl transition-all shadow-xl shadow-blue-900/40 hover:shadow-blue-900/60 hover:-translate-y-0.5 active:translate-y-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                  Get Started
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Link>
                <a href="#problem"
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium transition-colors group">
                  See how it works
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="group-hover:translate-y-0.5 transition-transform"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-5 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-400"><polyline points="20 6 9 17 4 12" /></svg>
                  <span>Deployed &amp; live on 3 boards</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-400"><polyline points="20 6 9 17 4 12" /></svg>
                  <span>ClickUp integration active</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-400"><polyline points="20 6 9 17 4 12" /></svg>
                  <span>GitHub build plans pushing</span>
                </div>
              </div>
            </div>

            {/* ── Right: product mockup ── */}
            <div className="hidden lg:block">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      {/* ── Clickbait ticker ── */}
      <div className="bg-blue-600 py-4 overflow-hidden border-y border-blue-500">
        <div className="flex gap-12 animate-[marquee_28s_linear_infinite] whitespace-nowrap w-max">
          {[
            "🔥 PMs love this one tool",
            "📉 Local backlog reduced 54% — engineers HATE this",
            "😤 134 already-shipped features caught red-handed",
            "🚀 Docs say 4–6 days to prod. We don't recommend waiting",
            "🧠 GPT-4o read 1,194 tickets so you don't have to",
            "👀 85 UI/UX wins hiding in your backlog right now",
            "🔥 PMs love this one tool",
            "📉 Local backlog reduced 54% — engineers HATE this",
            "😤 134 already-shipped features caught red-handed",
            "🚀 Docs say 4–6 days to prod. We don't recommend waiting",
            "🧠 GPT-4o read 1,194 tickets so you don't have to",
            "👀 85 UI/UX wins hiding in your backlog right now",
          ].map((text, i) => (
            <span key={i} className="text-sm font-semibold text-white flex items-center gap-12">
              {text}
              <span className="text-blue-300 text-lg">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── Stats bar ── */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-5xl mx-auto px-8 pt-10 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-3xl font-extrabold text-blue-600 mb-0.5">{s.value}</div>
              <div className="text-sm font-semibold text-slate-800">{s.label}</div>
              <div className="text-xs text-slate-400 mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>
        <div className="max-w-5xl mx-auto px-8 pb-5 text-center">
          <p className="text-[11px] text-slate-400 italic">* No PMs were harmed in the making of this dashboard. Several backlogs were.</p>
        </div>
      </section>

      {/* ── Problem ── */}
      <section id="problem" className="max-w-5xl mx-auto px-8 py-20 scroll-mt-14">
        <SectionEyebrow>The Problem</SectionEyebrow>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6 max-w-3xl leading-tight">
          GoHighLevel receives thousands of feature requests. None of them are connected to each other.
        </h2>

        <div className="grid sm:grid-cols-2 gap-5 mb-10">
          <ProblemCard title="Duplicates hide true demand">
            Ten customers requesting the same feature across ten posts fragments the signal. The true aggregate demand never surfaces. Decisions are made on louder posts, not more-requested ones.
          </ProblemCard>
          <ProblemCard title="Prioritization is gut feel">
            PMs read unstructured text under time pressure and make inconsistent judgment calls. There is no shared scoring model, no structured output, no audit trail.
          </ProblemCard>
          <ProblemCard title="Shipped work stays open">
            Features already in the changelog sit as open requests because no one has cross-referenced the two. Time gets spent triaging resolved problems.
          </ProblemCard>
          <ProblemCard title="Manual triage doesn&apos;t scale">
            The Automations board alone has 3,000+ posts. Reading them is a multi-hour task repeated every sprint. That time never stops compounding.
          </ProblemCard>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 rounded-2xl p-7">
          <div className="text-sm font-bold text-red-600 uppercase tracking-wider mb-2">The compounding risk</div>
          <p className="text-lg font-semibold text-slate-900 mb-2">
            AI execution tools make this problem worse, not better.
          </p>
          <p className="text-slate-600 leading-relaxed">
            AI coding tools give developers a 2-4x productivity boost. But if those tools are pointed at duplicates,
            already-shipped work, or low-priority noise, that boost multiplies wasted effort, not value.
            Speed without direction is just <strong>faster mistakes</strong>.
            What is missing is not more execution power.
            It is a demand intelligence layer that tells the team what is actually worth building.
          </p>
        </div>
      </section>

      {/* ── Solution: 3 pillars ── */}
      <section id="solution" className="bg-slate-900 text-white py-20 scroll-mt-14">
        <div className="max-w-5xl mx-auto px-8">
          <SectionEyebrow light>The Solution</SectionEyebrow>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4 max-w-3xl leading-tight">
            Three pillars that multiply what AI already gives you.
          </h2>
          <p className="text-slate-400 text-lg mb-12 max-w-2xl">
            Roadmap Radar is not another AI coding tool. It sits upstream of execution and tells every tool what to build, and in what order.
          </p>

          <div className="grid sm:grid-cols-3 gap-5">
            <PillarCard number="01" title="Signal Intelligence" subtitle="Any source. One picture." color="blue">
              Today: Canny. Next: Reddit discussions, YouTube comment threads,
              support tickets, Pendo analytics, GitHub issues. The pipeline works with any text-based source.
              <br /><br />
              One place to see everything customers are saying, across every channel.
            </PillarCard>
            <PillarCard number="02" title="The Multiplier" subtitle="Right work + AI speed = compounding returns." color="emerald">
              Grouped demand instead of scattered noise. Impact scores from the full data set.
              PRDs grounded in real customer pain points. Tasks with story point estimates, sprint-ready on arrival.
              <br /><br />
              AI execution speed applied to higher-quality inputs produces better results, not just faster ones.
            </PillarCard>
            <PillarCard number="03" title="Developer Control" subtitle="Not a constraint. A quality driver." color="violet">
              Fully autonomous AI commits are fast but unreliable. Developers who review a plan before running it
              catch edge cases, adapt to the real codebase, and own the output.
              <br /><br />
              AI handles the planning. Developers handle the execution. Higher trust. Better code. Faster over time.
            </PillarCard>
          </div>
        </div>
      </section>

      {/* ── How it works: Analysis Pipeline ── */}
      <section id="pipeline" className="max-w-5xl mx-auto px-8 py-20 scroll-mt-14">
        <SectionEyebrow>How It Works</SectionEyebrow>
        <h2 className="text-3xl font-extrabold tracking-tight mb-3">A nine-stage intelligence pipeline.</h2>
        <p className="text-slate-500 mb-12 max-w-xl">
          Every Canny post passes through structured AI processing.
          The result is a ranked, grouped demand model with full context, ready to act on.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PIPELINE_STEPS.map((step) => (
            <div key={step.n} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-200 hover:shadow-md hover:shadow-blue-50 transition-all">
              <div className="text-xs font-bold text-blue-500 tracking-widest mb-2">{step.n}</div>
              <div className="font-semibold text-slate-900 mb-1.5">{step.title}</div>
              <p className="text-sm text-slate-500 leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-100 rounded-2xl p-6 flex flex-col sm:flex-row gap-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-1">Why two search methods plus AI verification?</div>
            <p className="text-sm text-slate-600">
              Simple keyword search misses requests phrased differently. Pure AI similarity search misses exact word matches.
              Combining both, then verifying with GPT-4o, is significantly more accurate than either approach on its own.
            </p>
          </div>
        </div>
      </section>

      {/* ── Agent Pipeline ── */}
      <section id="agent" className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200 py-20 scroll-mt-14">
        <div className="max-w-5xl mx-auto px-8">
          <SectionEyebrow>Agent Pipeline</SectionEyebrow>
          <h2 className="text-3xl font-extrabold tracking-tight mb-3">From insight to sprint-ready task in two minutes.</h2>
          <p className="text-slate-500 mb-12 max-w-xl">
            One click on any group triggers a four-agent chain. The result lands in
            ClickUp as an Epic with story-pointed sub-tasks and a Cursor build plan per dev task in GitHub.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch gap-0 mb-10 rounded-2xl overflow-hidden border border-slate-200">
            {AGENT_STEPS.map((step, i) => (
              <div key={step.label} className="flex-1 bg-white p-5 relative">
                {i < AGENT_STEPS.length - 1 && (
                  <div className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 rounded-full bg-slate-100 border border-slate-200 items-center justify-center z-10">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </div>
                )}
                <div className="text-2xl mb-2">{step.icon}</div>
                <div className="font-bold text-slate-900 text-sm mb-0.5">{step.label}</div>
                <div className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-2">{step.model}</div>
                <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <OutputCard icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /></svg>} color="violet" title="ClickUp Epic">
              Full PRD as description. Story-pointed sub-tasks with [dev]/[qa]/[design] labels. QA card auto-created and moved to ready-for-qa.
            </OutputCard>
            <OutputCard icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12" /></svg>} color="slate" title="GitHub Build Plans">
              A step-by-step Cursor plan per dev task, pushed to a dedicated plans repo. The AI never has access to the product codebase.
            </OutputCard>
            <OutputCard icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>} color="emerald" title="Developer Workflow">
              Dev sees ClickUp comment with GitHub link. Opens plan in Cursor. Reviews and executes. Owns the commit. AI never touches the product codebase.
            </OutputCard>
          </div>
        </div>
      </section>

      {/* ── Vision: Multi-source ── */}
      <section id="vision" className="max-w-5xl mx-auto px-8 py-20 scroll-mt-14">
        <SectionEyebrow>The Vision</SectionEyebrow>
        <h2 className="text-3xl font-extrabold tracking-tight mb-3 max-w-2xl">
          One place for every signal, from every channel.
        </h2>
        <p className="text-slate-500 mb-10 max-w-xl">
          The pipeline works with any text-based source. Adding a new one takes hours, not weeks.
          Every additional source makes the prioritization picture more complete.
        </p>

        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-1 bg-blue-600 rounded-2xl p-6 text-white">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-200 mb-4">Live today</div>
            <div className="space-y-2">
              {SOURCES_NOW.map((s) => (
                <div key={s} className="flex items-center gap-2.5 text-sm font-medium">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  </div>
                  {s}
                </div>
              ))}
            </div>
          </div>
          <div className="flex-[3] bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Coming next: same pipeline, new source</div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {SOURCES_NEXT.map((s) => (
                <div key={s} className="flex items-center gap-2.5 text-sm text-slate-600 font-medium">
                  <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  </div>
                  {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Production status ── */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-5xl mx-auto px-8">
          <SectionEyebrow>Production Status</SectionEyebrow>
          <h2 className="text-2xl font-extrabold tracking-tight mb-8">Infrastructure provisioned. 4–6 dev days to production.</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { done: true, label: "9-stage analysis pipeline" },
              { done: true, label: "Full web application: Dashboard, Groups, Jobs" },
              { done: true, label: "GCP infrastructure provisioned" },
              { done: true, label: "AI agent pipeline with ClickUp + GitHub plans" },
              { done: true, label: "Deployment scripts (provision → build → deploy)" },
              { done: false, label: "GCS artifact storage (1–2 days)" },
              { done: false, label: "Production auth: Google OAuth (1 day)" },
              { done: false, label: "Observability + health checks (1.5 days)" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${item.done ? "bg-emerald-100" : "bg-slate-100"}`}>
                  {item.done
                    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-emerald-600"><polyline points="20 6 9 17 4 12" /></svg>
                    : <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-slate-400"><circle cx="12" cy="12" r="9" /></svg>
                  }
                </div>
                <span className={`text-sm font-medium ${item.done ? "text-slate-800" : "text-slate-500"}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="max-w-5xl mx-auto px-8 py-20">
        <SectionEyebrow>FAQ</SectionEyebrow>
        <h2 className="text-3xl font-extrabold tracking-tight mb-10">Common questions.</h2>
        <div className="divide-y divide-slate-200">
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem key={i} question={item.q} answer={item.a} />
          ))}
        </div>
      </section>

      {/* ── Closing / The Bet ── */}
      <section className="bg-[#0a0f1e] text-white py-24">
        <div className="max-w-5xl mx-auto px-8 relative">
          <div className="absolute top-0 right-0 w-[600px] h-[400px] bg-blue-600 opacity-[0.08] blur-[100px] pointer-events-none" />
          <div className="relative max-w-3xl">
            <SectionEyebrow light>The Bet</SectionEyebrow>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] mb-6">
              Developers with better inputs beat developers with more execution power.
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-10">
              Copilot, Codex, Claude Code. They are all making developers faster.
              The missing piece is knowing which problems are worth solving in the first place,
              with enough evidence to act confidently.
              <br /><br />
              Roadmap Radar fills that gap. It is built to work alongside any AI execution tool, not replace one.
              The more sources it ingests, the sharper the signal. The more developers trust the plan, the better the output.
              <strong className="text-white"> Teams with better demand intelligence will pull ahead, and that advantage will grow as execution tools get faster.</strong>
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors text-sm shadow-lg shadow-blue-900/40">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                Get started
              </Link>
              <a href="#problem"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold rounded-xl transition-colors text-sm backdrop-blur-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
                Learn more
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#060b18] text-slate-500 py-6 text-center text-xs">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Link href="/docs" className="hover:text-slate-300 transition-colors font-medium">Documentation</Link>
          <span className="text-slate-700">·</span>
          <Link href="/login" className="hover:text-slate-300 transition-colors font-medium">Sign in</Link>
        </div>
        Roadmap Radar · GoHighLevel Internal Tool · Dev Nitro 2026
      </footer>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-start justify-between gap-4 w-full text-left group"
      >
        <span className="text-[15px] font-semibold text-slate-900 group-hover:text-primary-700 transition-colors leading-snug">
          {question}
        </span>
        <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
          open ? "bg-primary-100 text-primary-600" : "bg-slate-100 text-slate-400 group-hover:bg-primary-50 group-hover:text-primary-500"
        }`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && (
        <p className="mt-3 text-[14px] text-slate-500 leading-relaxed max-w-3xl animate-fade-in">
          {answer}
        </p>
      )}
    </div>
  );
}

// ─── Hero Visual ──────────────────────────────────────────────────────────────

const MOCK_GROUPS = [
  {
    score: 8.2,
    tier: "HIGH",
    tierColor: "text-emerald-400",
    title: "Webhook support for automations",
    impact: "Enables enterprise workflow integrations — blocking expansion deals.",
    count: 60,
    board: "Automations",
    boardColor: "bg-sky-900/60 text-sky-300",
    dot: "bg-emerald-500",
  },
  {
    score: 7.1,
    tier: "HIGH",
    tierColor: "text-emerald-400",
    title: "Custom domain for funnels",
    impact: "Critical for white-label agencies — affects plan upgrades.",
    count: 45,
    board: "Funnels",
    boardColor: "bg-violet-900/60 text-violet-300",
    dot: "bg-emerald-500",
  },
  {
    score: 6.4,
    tier: "MID",
    tierColor: "text-amber-400",
    title: "SMS reply threading",
    impact: "Affects 30% of conversation workflows — reduces churn.",
    count: 38,
    board: "Conversations",
    boardColor: "bg-amber-900/50 text-amber-300",
    dot: "bg-amber-400",
  },
];

function HeroVisual() {
  return (
    <div className="relative animate-[heroFloat_6s_ease-in-out_infinite]">
      {/* Floating stat chips above the card */}
      <div className="flex gap-2 mb-3 justify-end">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/10 text-[11px] font-semibold text-slate-300 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
          1,194 posts analyzed
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/10 text-[11px] font-semibold text-slate-300 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          548 groups
        </div>
      </div>

      {/* App window card */}
      <div className="bg-[#0d1526] rounded-2xl ring-1 ring-white/10 shadow-2xl shadow-blue-500/10 overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-[#0a1120]">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
          </div>
          <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-white/5 text-[10px] text-slate-500 font-mono text-center">
            roadmap-radar / Feature Groups
          </div>
          <div className="w-3 h-3 rounded-full bg-emerald-500/80 ring-2 ring-emerald-500/20" title="Live" />
        </div>

        {/* Group list */}
        <div className="p-3 space-y-2">
          {MOCK_GROUPS.map((g, i) => (
            <div key={i}
              className={`rounded-xl p-3 ring-1 transition-all ${
                i === 0
                  ? "bg-blue-600/10 ring-blue-500/30"
                  : "bg-white/[0.03] ring-white/[0.06]"
              }`}>
              <div className="flex items-start gap-3">
                {/* Score */}
                <div className="shrink-0 text-center w-8 pt-0.5">
                  <div className={`text-base font-extrabold leading-none ${g.tierColor}`}>{g.score}</div>
                  <div className={`text-[8px] font-bold uppercase tracking-widest mt-1 ${g.tierColor} opacity-70`}>{g.tier}</div>
                </div>
                <div className="w-px self-stretch bg-white/10 shrink-0" />
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-[12px] font-semibold text-white leading-snug line-clamp-1">{g.title}</p>
                    {i === 0 && (
                      <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/30 text-blue-300 ring-1 ring-blue-400/30">
                        PRD ready
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed mb-2 line-clamp-1">{g.impact}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} />
                      {g.count} requests
                    </span>
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${g.boardColor}`}>
                      {g.board}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Footer */}
          <div className="flex items-center justify-between px-1 pt-1">
            <span className="text-[10px] text-slate-600">Showing 548 of 1,194 posts</span>
            <span className="text-[10px] text-blue-500 font-medium">Sort: Impact ↓</span>
          </div>
        </div>
      </div>

      {/* Subtle glow behind the card */}
      <div className="absolute inset-0 -z-10 blur-3xl opacity-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-3xl" />
    </div>
  );
}

function SectionEyebrow({ children, light }: { children: React.ReactNode; light?: boolean }) {
  return (
    <div className={`text-xs font-bold uppercase tracking-[0.15em] mb-3 ${light ? "text-blue-400" : "text-blue-600"}`}>
      {children}
    </div>
  );
}

function ProblemCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:border-red-200 hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-red-500"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </div>
        <div>
          <div className="font-semibold text-slate-900 mb-1 text-sm">{title}</div>
          <p className="text-sm text-slate-500 leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  );
}

function PillarCard({ number, title, subtitle, color, children }: {
  number: string; title: string; subtitle: string; color: "blue" | "emerald" | "violet"; children: React.ReactNode;
}) {
  const accent = { blue: "text-blue-400 border-blue-500/30", emerald: "text-emerald-400 border-emerald-500/30", violet: "text-violet-400 border-violet-500/30" }[color];
  return (
    <div className={`bg-white/5 border rounded-2xl p-6 ${accent} hover:bg-white/10 transition-colors`}>
      <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${accent.split(" ")[0]}`}>{number}</div>
      <div className="text-lg font-bold text-white mb-1">{title}</div>
      <div className="text-xs text-slate-400 mb-4 font-medium">{subtitle}</div>
      <p className="text-sm text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}

function OutputCard({ icon, color, title, children }: {
  icon: React.ReactNode; color: "violet" | "slate" | "emerald"; title: string; children: React.ReactNode;
}) {
  const bg = { violet: "bg-violet-50 border-violet-100 text-violet-600", slate: "bg-slate-100 border-slate-200 text-slate-600", emerald: "bg-emerald-50 border-emerald-100 text-emerald-600" }[color];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className={`w-8 h-8 rounded-lg ${bg} border flex items-center justify-center mb-3`}>{icon}</div>
      <div className="font-semibold text-slate-900 text-sm mb-2">{title}</div>
      <p className="text-xs text-slate-500 leading-relaxed">{children}</p>
    </div>
  );
}
